import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, Sparkles } from "lucide-react";

const STORAGE_KEY = "vv-coming-soon-dismissed";
const HIDDEN_PATHS = ["/checkout", "/order-success", "/payment", "/admin", "/auth", "/staff-setup", "/payment/result"];

export default function ComingSoonBanner() {
  const location = useLocation();
  const reduce = useReducedMotion();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const [visible, setVisible] = useState(false);

  const hidden = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (hidden || dismissed) { setVisible(false); return; }
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [hidden, dismissed]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && visible) close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible]);

  const close = () => {
    setVisible(false);
    setDismissed(true);
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch {}
  };

  const reopen = () => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    setDismissed(false);
    setVisible(true);
  };

  const transition = reduce ? { duration: 0 } : { duration: 0.5, ease: [0.22, 1, 0.36, 1] };

  if (hidden) return null;

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.aside
            data-testid="coming-soon-banner"
            role="complementary"
            aria-label="Coming soon announcement"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
            transition={transition}
            className="fixed bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 z-[70] sm:w-[360px] bg-[#F5F0E7] text-[#26382C] border border-[#B59A65]/40 shadow-[0_24px_60px_-20px_rgba(38,56,44,0.35)]"
          >
            <button
              data-testid="coming-soon-close"
              onClick={close}
              aria-label="Dismiss announcement"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-[#26382C]/60 hover:text-[#26382C] transition-colors"
            >
              <X size={15} strokeWidth={1.5} />
            </button>
            <div className="p-7 sm:p-8">
              <p data-testid="coming-soon-label" className="text-[9px] uppercase tracking-[0.35em] text-[#B59A65] mb-4 relative overflow-hidden">
                ✦ Exclusive Preview ✦
                {!reduce && (
                  <motion.span
                    aria-hidden
                    className="absolute inset-y-0 w-12 bg-gradient-to-r from-transparent via-[#B59A65]/25 to-transparent"
                    initial={{ x: "-150%" }}
                    animate={{ x: "350%" }}
                    transition={{ duration: 3.2, repeat: Infinity, repeatDelay: 4, ease: "easeInOut" }}
                  />
                )}
              </p>
              <h2 data-testid="coming-soon-heading" className="font-display text-xl sm:text-2xl leading-snug tracking-tight">
                SOMETHING EXTRAORDINARY IS COMING.
              </h2>
              <p className="text-[13px] leading-relaxed mt-4 text-[#26382C]/80 font-light">
                Something beautiful is on its way. We’re bringing you fresh styles, timeless elegance, and a little something worth waiting for.
              </p>
              <p className="text-[13px] leading-relaxed mt-3 text-[#26382C]/80 font-light">
                Stay close. Your next favourite look is almost here. ✨
              </p>
              <div className="mt-6 pt-5 border-t border-[#B59A65]/30">
                <p className="text-[10px] tracking-[0.3em] uppercase">Velour &amp; Vane™</p>
                <p className="text-[9px] tracking-[0.25em] uppercase text-[#B59A65] mt-1">Beyond the Ordinary.</p>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {dismissed && !visible && (
          <motion.button
            data-testid="coming-soon-reopen"
            onClick={reopen}
            aria-label="Show coming soon announcement"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={reduce ? { duration: 0 } : { duration: 0.35 }}
            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[70] w-11 h-11 rounded-full bg-[#F5F0E7] border border-[#B59A65]/40 text-[#B59A65] shadow-[0_12px_30px_-10px_rgba(38,56,44,0.35)] flex items-center justify-center hover:text-[#26382C] transition-colors"
          >
            <Sparkles size={16} strokeWidth={1.5} />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
